import { api } from '@/lib/api';
import type {
  TeamResponse,
  TeamQuery,
  TeamListResponse,
  CreateTeamRequest,
  UpdateTeamRequest,
} from '@troia-v3/types';

/** Item de equipe na rota `/teams/dropdown`. */
export interface TeamDropdownItem {
  id: string;
  name: string;
  description?: string;
  status: 'active' | 'inactive';
  createdAt: string;
  updatedAt: string;
}

export const teamsService = {
  /**
   * Get all teams with pagination and filters
   */
  async getTeams(query?: TeamQuery): Promise<TeamListResponse> {
    const response = await api.get('/teams', { params: query });
    return response.data.data;
  },

  /**
   * Get team by ID
   */
  async getTeamById(id: string): Promise<TeamResponse> {
    const response = await api.get(`/teams/${id}`);
    return response.data.data;
  },

  /**
   * Create new team
   */
  async createTeam(data: CreateTeamRequest): Promise<TeamResponse> {
    const response = await api.post('/teams', data);
    return response.data.data;
  },

  /**
   * Update team
   */
  async updateTeam(id: string, data: UpdateTeamRequest): Promise<TeamResponse> {
    const response = await api.put(`/teams/${id}`, data);
    return response.data.data;
  },

  /**
   * Delete team
   */
  async deleteTeam(id: string): Promise<void> {
    await api.delete(`/teams/${id}`);
  },

  /**
   * Activate team
   */
  async activateTeam(id: string): Promise<TeamResponse> {
    const response = await api.patch(`/teams/${id}/activate`);
    return response.data.data;
  },

  /**
   * Deactivate team
   */
  async deactivateTeam(id: string): Promise<TeamResponse> {
    const response = await api.patch(`/teams/${id}/deactivate`);
    return response.data.data;
  },

  /**
   * Get teams for dropdown — `GET /teams/dropdown` (sem permissionMiddleware).
   * Campo a campo espelha a projeção do backend (`teams/repository.ts` →
   * `findAllForDropdown`).
   */
  async getTeamsForDropdown(): Promise<TeamDropdownItem[]> {
    const response = await api.get('/teams/dropdown');
    return response.data.data;
  },

  /**
   * Get team members
   */
  async getTeamMembers(teamId: string): Promise<any[]> {
    const response = await api.get(`/teams/${teamId}/members`);
    return response.data.data;
  },

  /**
   * Add user to team
   */
  async addUserToTeam(data: { teamId: string; userId: string; role?: string }): Promise<any> {
    const response = await api.post('/teams/users', data);
    return response.data.data;
  },

  /**
   * Remove user from team
   */
  async removeUserFromTeam(teamId: string, userId: string): Promise<void> {
    await api.delete(`/teams/users/${teamId}/${userId}`);
  },
};
